import React, { useMemo } from 'react'
import { Photo } from '../Photo'
import { useGlobalState } from '../../hooks'
import './AttendeeStack.css'

const MAX_VISIBLE = 3

// Fisher-Yates shuffle, used to pick a random subset of the *other* attendees (not the
// current user, who's placed separately below).
const shuffled = (arr) => {
  const copy = [...arr]
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

// Overlapping avatar stack of the people taking part in something; the whole stack is one
// button that toggles the logged-in user's own participation.
//
// Was two identical copies — the events app's EventAttendees (marking interest in an event)
// and recruitment's InterviewAttendees (signing up for an interview slot) — because it is
// the same gesture. Promoted here, MonthCalendar-style, when the landing page's
// ExternalEventItem became a third caller. Three surfaces now share it: an events row, an
// event's detail page, and an Intervjuprocess round cell.
//
// Domain wording stays with the caller: `joinTitle`/`leaveTitle` are the button's tooltip
// ("Markera intresse"/"Ta bort intresse" for an event, "Anmäl mig"/"Avanmäl mig" for an
// interview). This component knows nothing about either app.
//
// Shows at most 3 avatars: if the current user is taking part they are always visible and
// always frontmost; remaining slots are filled with a random subset of the others.
//
// `compact` reproduces the smaller chip sizing the Intervjuprocess round cells have always
// used, so promoting this component was a pixel-for-pixel no-op on all three surfaces.
export const AttendeeStack = ({
  users = [],
  isParticipant,
  onToggle,
  joinTitle,
  leaveTitle,
  compact,
}) => {
  const [loggedInUser] = useGlobalState('loggedInUser')

  // Memoized on a stable signature of *who* is in the list, deliberately not on the array's
  // identity. The landing-page ActivityWidget refetches every 60s and hands each row a brand
  // new attendee array; keyed on identity, that recomputed the random subset and the visible
  // faces visibly swapped on their own every minute in an always-on-screen dock. Both
  // original copies had this — only the widget's polling made it observable.
  const userIdSignature = users.map(u => u.user_id).sort().join(',')
  const loggedInUserId = loggedInUser && loggedInUser.user_id

  const visible = useMemo(() => {
    const currentUser = loggedInUserId && users.find(u => u.user_id === loggedInUserId)
    const others = currentUser ? users.filter(u => u.user_id !== currentUser.user_id) : users
    const remainingSlots = currentUser ? MAX_VISIBLE - 1 : MAX_VISIBLE
    const randomOthers = shuffled(others).slice(0, remainingSlots)
    return currentUser ? [currentUser, ...randomOthers] : randomOthers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdSignature, loggedInUserId])

  const overflow = users.length - visible.length

  return (
    <button
      type="button"
      className={"attendee-stack" + (isParticipant ? " active" : "") + (compact ? " compact" : "")}
      // Both calls are load-bearing: this button sits inside a row whose main area is a
      // react-router <Link>, and a bare click would navigate away instead of toggling.
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle() }}
      title={isParticipant ? leaveTitle : joinTitle}
    >
      <div className="attendee-stack-photos">
        {visible.map(u => (
          <Photo
            key={u.user_id}
            data={u.user_photo}
            alt={u.user_fullname}
            tooltip={u.user_fullname}
            size="small"
            className={u.user_id === loggedInUserId ? "attendee-stack-you" : ""}
          />
        ))}
        {overflow > 0 && <div className="attendee-stack-overflow">+{overflow}</div>}
        {users.length === 0 && <div className="attendee-stack-empty">+</div>}
      </div>
    </button>
  )
}
